/**
 * Messages API
 *
 * Functions for interacting with the messages endpoints.
 * Admin endpoints use org-scoped routes, crew endpoints use /me routes.
 */

import { api } from './client';

// =============================================================================
// Types
// =============================================================================

export type MessageType =
  | 'BROADCAST'
  | 'ASSIGNMENT'
  | 'CREW_REPLY'
  | 'EVENT_PUBLISHED'
  | 'EVENT_UPDATED';

export type DeliveryStatus = 'QUEUED' | 'SENT' | 'DELIVERED' | 'FAILED';

export interface MessageRecipient {
  id: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  emailStatus: DeliveryStatus;
  smsStatus: DeliveryStatus | null;
  readAt: string | null;
}

export interface MessageReply {
  id: string;
  messageType: MessageType;
  subject: string;
  body: string;
  sentAt: string;
  sentBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface Message {
  id: string;
  subject: string;
  body: string;
  messageType: MessageType;
  channels: string[]; // ['EMAIL', 'SMS']
  eventId: string | null;
  event?: {
    id: string;
    name: string;
  } | null;
  sentAt: string;
  sentBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  recipientCount?: number;
  replyCount?: number;
  recipients?: MessageRecipient[];
  replies?: MessageReply[];
  // Delivery stats (populated on detail)
  deliveryStats?: {
    total: number;
    emailSent: number;
    emailDelivered: number;
    emailFailed: number;
    smsSent: number;
    smsDelivered: number;
    smsFailed: number;
    read: number;
  };
}

// Crew inbox message (includes org info)
export interface InboxMessage {
  id: string;
  subject: string;
  body: string;
  messageType: MessageType;
  channels: string[];
  sentAt: string;
  sentBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  readAt: string | null;
  replies?: MessageReply[];
}

// =============================================================================
// Request/Response Types
// =============================================================================

export interface SendMessageInput {
  subject: string;
  body: string;
  recipientUserIds?: string[];
  sendToAllCrew?: boolean;
  targetEventId?: string;
  /** Append the event's details to the delivered email (default true). */
  includeEventInfo?: boolean;
  sendViaEmail?: boolean;
  sendViaSms?: boolean;
}

export interface ListMessagesParams {
  messageType?: MessageType;
  page?: number;
  limit?: number;
}

export interface ListMessagesResponse {
  messages: Message[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface GetMyMessagesParams {
  unreadOnly?: boolean;
  orgSlug?: string;
  page?: number;
  limit?: number;
}

export interface GetMyMessagesResponse {
  messages: InboxMessage[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ReplyInput {
  body: string;
}

// =============================================================================
// Admin API (Org-scoped)
// =============================================================================

/**
 * Send a message to recipients
 */
export async function sendMessage(data: SendMessageInput): Promise<{ message: string; messageId: string }> {
  const response = await api.post<{ message: string; messageId: string }>('/messages', data);
  return response.data;
}

/**
 * List sent messages for the organization
 */
export async function listMessages(params?: ListMessagesParams): Promise<ListMessagesResponse> {
  const response = await api.get<ListMessagesResponse>('/messages', { params });
  return response.data;
}

/**
 * Get a single message with delivery details
 */
export async function getMessage(id: string): Promise<{ message: Message }> {
  const response = await api.get<{ message: Message }>(`/messages/${id}`);
  return response.data;
}

/**
 * Reply to a message thread (admin)
 */
export async function adminReplyToMessage(id: string, data: ReplyInput): Promise<{ message: string; id: string }> {
  const response = await api.post<{ message: string; id: string }>(`/messages/${id}/reply`, data);
  return response.data;
}

// =============================================================================
// Crew API (User-scoped /me routes)
// =============================================================================

/**
 * Get the current user's inbox messages
 */
export async function getMyMessages(params?: GetMyMessagesParams): Promise<GetMyMessagesResponse> {
  const response = await api.get<GetMyMessagesResponse>('/me/messages', { params });
  return response.data;
}

/**
 * Get a single inbox message
 */
export async function getMyMessage(id: string): Promise<{ message: InboxMessage }> {
  const response = await api.get<{ message: InboxMessage }>(`/me/messages/${id}`);
  return response.data;
}

/**
 * Mark a message as read
 */
export async function markMessageAsRead(id: string): Promise<{ message: string }> {
  const response = await api.patch<{ message: string }>(`/me/messages/${id}/read`);
  return response.data;
}

/**
 * Reply to a message (goes to all org admins)
 */
export async function replyToMessage(id: string, data: ReplyInput): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(`/me/messages/${id}/reply`, data);
  return response.data;
}

/**
 * Get unread message count
 */
export async function getUnreadCount(): Promise<{ count: number }> {
  const response = await api.get<{ count: number }>('/me/messages/unread-count');
  return response.data;
}
